import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
    RefreshCw,
    Trash2,
    Check,
    X,
    Search,
    Lock,
    Unlock,
} from "lucide-react";

// ---------- Users Tab ----------
export default function UsersTab() {
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState("");
    const [search, setSearch] = useState("");
    const [confirm, setConfirm] = useState(null); // {title, message, onConfirm}

    const load = useCallback(async () => {
        const params = filter ? { status: filter } : {};
        const { data } = await api.get("/admin/users", { params });
        setUsers(data);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (id, status) => {
        await api.put(`/admin/users/${id}/status`, { status });
        load();
    };

    const toggleSpecsView = async (id, can) => {
        await api.put(`/admin/users/${id}/permissions`, { can_view_specs: can });
        load();
    };

    const togglePricesView = async (id, can) => {
        await api.put(`/admin/users/${id}/permissions`, { can_view_prices: can });
        load();
    };

    const toggleStockQtyView = async (id, can) => {
        await api.put(`/admin/users/${id}/permissions`, { can_view_stock_qty: can });
        load();
    };

    const askDelete = (u) => {
        setConfirm({
            title: "Delete user",
            message: `Permanently delete "${u.email}"? This cannot be undone.`,
            destructive: true,
            onConfirm: async () => {
                try {
                    await api.delete(`/admin/users/${u.id}`);
                } catch (e) {
                    alert(e.response?.data?.detail || "Delete failed");
                }
                setConfirm(null);
                load();
            },
        });
    };

    const filtered = users.filter((u) =>
        !search || u.email.toLowerCase().includes(search.toLowerCase()) || u.name.toLowerCase().includes(search.toLowerCase())
    );

    const pill = (status) => {
        const cls = {
            pending: "bg-amber-100 text-amber-800",
            approved: "bg-emerald-100 text-emerald-800",
            rejected: "bg-red-100 text-red-800",
            admin: "bg-slate-900 text-white",
        };
        return cls[status] || "bg-slate-100 text-slate-700";
    };

    return (
        <div data-testid="admin-users-tab">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by email or name..."
                        className="input-field !pl-9"
                        data-testid="users-search"
                    />
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-field !w-auto" data-testid="users-filter">
                    <option value="">All users</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>
                <button onClick={load} className="btn-outline"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
            </div>

            <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-left">
                            <th className="px-3 py-2 label-kicker">Name</th>
                            <th className="px-3 py-2 label-kicker">Email</th>
                            <th className="px-3 py-2 label-kicker">Role</th>
                            <th className="px-3 py-2 label-kicker">Status</th>
                            <th className="px-3 py-2 label-kicker">Specs</th>
                            <th className="px-3 py-2 label-kicker">Prices</th>
                            <th className="px-3 py-2 label-kicker">Stock qty</th>
                            <th className="px-3 py-2 label-kicker">Joined</th>
                            <th className="px-3 py-2 label-kicker text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan="9" className="px-3 py-8 text-center font-mono text-slate-500">No users.</td></tr>
                        )}
                        {filtered.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                                <td className="px-3 py-2 font-semibold">{u.name}</td>
                                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${pill(u.role === "admin" ? "admin" : "")}`}>
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${pill(u.status)}`}>
                                        {u.status}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    {u.role !== "admin" ? (
                                        <button
                                            onClick={() => toggleSpecsView(u.id, !u.can_view_specs)}
                                            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold uppercase ${
                                                u.can_view_specs
                                                    ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                    : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                                            }`}
                                            title={u.can_view_specs ? "Click to block specs" : "Click to allow specs"}
                                            data-testid={`specs-toggle-${u.id}`}
                                        >
                                            {u.can_view_specs ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                            {u.can_view_specs ? "Allowed" : "Blocked"}
                                        </button>
                                    ) : (
                                        <span className="text-[10px] font-mono text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    {u.role !== "admin" ? (
                                        <button
                                            onClick={() => togglePricesView(u.id, !(u.can_view_prices ?? true))}
                                            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold uppercase ${
                                                (u.can_view_prices ?? true)
                                                    ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                    : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                                            }`}
                                            title={(u.can_view_prices ?? true) ? "Click to hide prices" : "Click to show prices"}
                                            data-testid={`prices-toggle-${u.id}`}
                                        >
                                            {(u.can_view_prices ?? true) ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                            {(u.can_view_prices ?? true) ? "Allowed" : "Blocked"}
                                        </button>
                                    ) : (
                                        <span className="text-[10px] font-mono text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    {u.role !== "admin" ? (
                                        <button
                                            onClick={() => toggleStockQtyView(u.id, !(u.can_view_stock_qty ?? false))}
                                            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold uppercase ${
                                                (u.can_view_stock_qty ?? false)
                                                    ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                    : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                                            }`}
                                            title={(u.can_view_stock_qty ?? false) ? "Click to hide stock qty" : "Click to show stock qty"}
                                            data-testid={`stockqty-toggle-${u.id}`}
                                        >
                                            {(u.can_view_stock_qty ?? false) ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                            {(u.can_view_stock_qty ?? false) ? "Allowed" : "Blocked"}
                                        </button>
                                    ) : (
                                        <span className="text-[10px] font-mono text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                                    {new Date(u.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex items-center gap-1">
                                        <button
                                            onClick={async () => {
                                                const pwd = prompt(`Reset password për ${u.email}?\n\nFut password-in e ri (min 6 karaktere):`);
                                                if (!pwd) return;
                                                if (pwd.length < 6) { alert("Password duhet të jetë të paktën 6 karaktere"); return; }
                                                try {
                                                    await api.put(`/admin/users/${u.id}/password`, { new_password: pwd });
                                                    alert(`✓ Password u ndryshua për ${u.email}`);
                                                } catch (e) {
                                                    alert("Gabim: " + (e.response?.data?.detail || e.message));
                                                }
                                            }}
                                            className="p-1.5 text-amber-700 hover:bg-amber-50"
                                            title="Reset password"
                                            data-testid={`reset-pwd-${u.id}`}
                                        ><Lock className="w-4 h-4" /></button>
                                    {u.role !== "admin" && (
                                        <>
                                            {u.status !== "approved" && (
                                                <button
                                                    onClick={() => changeStatus(u.id, "approved")}
                                                    className="p-1.5 text-emerald-700 hover:bg-emerald-50"
                                                    title="Approve"
                                                    data-testid={`approve-${u.id}`}
                                                ><Check className="w-4 h-4" /></button>
                                            )}
                                            {u.status !== "rejected" && (
                                                <button
                                                    onClick={() => changeStatus(u.id, "rejected")}
                                                    className="p-1.5 text-red-700 hover:bg-red-50"
                                                    title="Reject"
                                                    data-testid={`reject-${u.id}`}
                                                ><X className="w-4 h-4" /></button>
                                            )}
                                            <button
                                                onClick={() => askDelete(u)}
                                                className="p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                                                title="Delete"
                                                data-testid={`delete-user-${u.id}`}
                                            ><Trash2 className="w-4 h-4" /></button>
                                        </>
                                    )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                open={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                confirmText="Delete"
                destructive={confirm?.destructive}
                onConfirm={() => confirm?.onConfirm()}
                onCancel={() => setConfirm(null)}
            />
        </div>
    );
}
